import { type ChangeEvent, useCallback, useState } from 'react';
import type { DateValue } from '@internationalized/date';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfHoursV1OperatingHoursServiceGetOperatingHours } from '@interxfi/api-client/generated/hooks/ixf-hours-v1-operating-hours-service-get-operating-hours';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { PageTabRail } from '@interxfi/app-components/components/page-tab-rail/page-tab-rail';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SelectInput } from '@interxfi/design-system/components/input';
import { ComboboxInput } from '@interxfi/design-system/components/input/combobox-input/combobox-input';
import { StandaloneCalendar } from '@interxfi/design-system/components/input/date-picker-input/calendar/standalone-calendar';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import AchIcon from '@interxfi/design-system/icons/react/icon-ach';
import BoltIcon from '@interxfi/design-system/icons/react/icon-bolt';
import HalfSunIcon from '@interxfi/design-system/icons/react/icon-halfsun';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import SunIcon from '@interxfi/design-system/icons/react/icon-sun';
import WireIcon from '@interxfi/design-system/icons/react/icon-wires';
import { usePreferredTimeZone } from '@interxfi/design-system/time-zone-context';
import { useCurrentDate } from '@interxfi/design-system/util';
import type { IxfHoursV1OperatingDay } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../components/router/router-tabbed-navigation';
import { SCHEDULE_OPTIONS, type ScheduleName, useTimeZoneOptions } from '../../../../../features/hours/helpers';
import { OperatingHoursBox } from '../../../../../features/hours/operating-hours-box/operating-hours-box';
import type { OperatingDay } from '../../../../../features/hours/operating-hours-box/operating-hours-day-box';
import { useHoursScheduleContext } from '../../../../../features/hours/schedule-context/hours-schedule-context';
import { HoursScheduleContextBar } from '../../../../../features/hours/schedule-context/hours-schedule-context-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../util/error-handler';
import { getPageHead } from '../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/hours/_hours/')({
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'hours-summary',
        label: 'Hours',
        to: '/hours',
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Hours,
      label: 'Calendar',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Hours>,
  head: () => getPageHead('Hours'),
  component: HoursCalendar,
});

const sharedColumnConfig = {
  width: { type: 'min', min: 320 },
} as const satisfies Omit<WrappingLayoutColumnDef, 'render'>;

function apiOperatingDayToOperatingDay(day: IxfHoursV1OperatingDay | undefined, scheduleName: ScheduleName): OperatingDay {
  const baseOperatingDay = {
    date: day?.date,
    exception: day?.exception,
    items: STABLE_EMPTY_ARRAY_REF,
  } as const satisfies OperatingDay;

  switch (scheduleName) {
    case 'trading':
      return {
        ...baseOperatingDay,
        items: [
          {
            id: 'early',
            label: 'Early',
            icon: <HalfSunIcon aria-label="Early icon" height={16} />,
            hours: day?.trading?.early?.effective,
            defaultHours: day?.trading?.early?.default,
          },
          {
            id: 'core',
            label: 'Core',
            icon: <SunIcon aria-label="Core icon" height={16} />,
            hours: day?.trading?.core?.effective,
            defaultHours: day?.trading?.core?.default,
          },
          {
            id: 'late',
            label: 'Late',
            icon: <HalfSunIcon aria-label="Late icon" height={16} />,
            hours: day?.trading?.late?.effective,
            defaultHours: day?.trading?.late?.default,
          },
        ],
      };
    case 'transfers':
      return {
        ...baseOperatingDay,
        items: [
          {
            id: 'wire',
            icon: <WireIcon aria-label="Wire icon" height={16} />,
            label: 'Wire',
            hours: day?.transfers?.wire?.effective,
            defaultHours: day?.transfers?.wire?.default,
          },
          {
            id: 'ach',
            icon: <AchIcon aria-label="Standard ACH icon" height={16} />,
            label: 'ACH Standard',
            hours: day?.transfers?.achStandard?.effective,
            defaultHours: day?.transfers?.achStandard?.default,
          },
          {
            id: 'sameDayAch',
            icon: <BoltIcon aria-label="Same-Day ACH icon" height={16} />,
            label: 'ACH Same-Day',
            hours: day?.transfers?.achSameDay?.effective,
            defaultHours: day?.transfers?.achSameDay?.default,
          },
        ],
      };
    case 'moneyMarket':
      return {
        ...baseOperatingDay,
        items: [
          {
            id: 'orders',
            icon: <OrdersIcon aria-label="Orders icon" height={16} />,
            label: 'Orders',
            hours: day?.moneyMarket?.orders?.effective,
            defaultHours: day?.moneyMarket?.orders?.default,
          },
        ],
      };
    default:
      exhaustiveCheck(scheduleName);
      return baseOperatingDay;
  }
}

const errorHandler = buildDefaultErrorMessageGetter('Failed to load hours data for the selected date', 'view hours data');

function HoursCalendar() {
  const preferredTimeZone = usePreferredTimeZone();
  const [timeZone, setTimeZone] = useState(preferredTimeZone);
  const timeZoneOptions = useTimeZoneOptions();
  const { zonedDate } = useCurrentDate(timeZone);
  const [selectedDate, setSelectedDate] = useState<DateValue>(zonedDate);
  const { exposure, region, schedule, onChangeSchedule } = useHoursScheduleContext();
  const { data, error } = useIxfHoursV1OperatingHoursServiceGetOperatingHours(
    { date: selectedDate.toDate(timeZone).toLocaleDateString('en-CA'), timezone: timeZone, key: region, exposure },
    { refetchInterval: false, refetchOnMount: false, refetchOnWindowFocus: false },
  );
  useErrorHandler(error, errorHandler);
  const handleTimeZoneChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) {
      return;
    }

    setTimeZone(e.target.value);
  }, []);

  const handleScheduleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onChangeSchedule(e.target.value as ScheduleName);
    },
    [onChangeSchedule],
  );

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageTabRail actions={<HoursScheduleContextBar />} group={TabbedNavigationGroup.Hours} />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...sharedColumnConfig,
            render: (
              <VerticalStack gap={7}>
                <StandaloneCalendar onChange={setSelectedDate} timeZone={timeZone} value={selectedDate} />

                {timeZoneOptions?.length ? (
                  <ComboboxInput
                    allowClear={false}
                    label="Time Zone"
                    name="timeZone"
                    onChange={handleTimeZoneChange}
                    options={timeZoneOptions}
                    value={timeZone}
                  />
                ) : null}
              </VerticalStack>
            ),
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...sharedColumnConfig,
            render: (
              <VerticalStack gap={4}>
                <SelectInput
                  allowClear={false}
                  label="Schedule"
                  name="schedule"
                  onChange={handleScheduleChange}
                  options={SCHEDULE_OPTIONS}
                  value={schedule}
                />

                <OperatingHoursBox
                  t1={apiOperatingDayToOperatingDay(
                    data?.t1?.[schedule] ? { ...data.t1[schedule], [schedule]: data.t1[schedule].hours } : undefined,
                    schedule,
                  )}
                  timeZone={timeZone}
                  today={apiOperatingDayToOperatingDay(data?.today, schedule)}
                  tomorrow={apiOperatingDayToOperatingDay(data?.tomorrow, schedule)}
                />
              </VerticalStack>
            ),
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
